import axios from 'axios'
import { getResponseData, normalizeQueryError } from 'src/queries/network.utils'
import { getRequestConfig, RequestConfig } from 'src/queries/requestConfig'

import {
  AccountCollectionFlowCompletionResponse,
  AccountCollectionFlowResponse,
  CompleteFundingAccountVerificationRequest,
  CreatePaymentConnectionRequest,
  CreatePaymentConnectionResponse,
  FundingAccountVerificationResponse,
  PaymentConnectionResponse,
  PaymentFundingAccountDetails,
} from './payments.types'
import { extractKybValidationError } from './paymentsKybError'

const CONNECTION_BASE = '/payments/connection'
const ACCOUNT_COLLECTION_FLOW_PATH = '/payments/account-collection-flow'
const FUNDING_ACCOUNT_PATH = '/payments/funding-account'
const FUNDING_ACCOUNT_VERIFICATION_PATH =
  '/payments/funding-account/verification'

// ─── Connection & KYB ────────────────────────────────────────────

export async function getPaymentConnection(): Promise<PaymentConnectionResponse> {
  return getResponseData(
    axios.get<PaymentConnectionResponse>(
      CONNECTION_BASE,
      getRequestConfig(RequestConfig.Default),
    ),
  )
}

export async function getFundingAccount(): Promise<PaymentFundingAccountDetails> {
  return getResponseData(
    axios.get<PaymentFundingAccountDetails>(
      FUNDING_ACCOUNT_PATH,
      getRequestConfig(RequestConfig.Default),
    ),
  )
}

/** `POST /payments/connection` — KYB-ONLY (the split flow). The full KYB PII
 * (SSNs, DOBs, beneficial-owner identities, document bytes) transits to the
 * provider on this single call and is never persisted, logged, or cached; only
 * `legalEntityId` + `kybStatus` come back on the summary (ADR-0005). */
export async function submitLegalEntity(
  request: CreatePaymentConnectionRequest,
): Promise<CreatePaymentConnectionResponse> {
  try {
    const response = await axios.post<CreatePaymentConnectionResponse>(
      CONNECTION_BASE,
      request,
      getRequestConfig(RequestConfig.Default),
    )
    return response.data
  } catch (error) {
    // A KYB validation 422 carries field-level errors the form must surface —
    // throw a typed error the mutation's onError branches on (never the silent
    // pending dead-end). Everything else falls back to the normalized error.
    const validationError = extractKybValidationError(error)
    if (validationError) throw validationError
    throw normalizeQueryError(error)
  }
}

/**
 * Bootstrap the embedded Modern Treasury Account Collection Flow for the
 * org's own funding (pay-from) account — `POST /payments/account-collection-flow`.
 * No routing/account number is sent here at all — the customer types their
 * bank details into MT's own iframe (`MtAccountCollection`), MT creates the
 * ExternalAccount, then the app calls `completeAccountCollectionFlow(flowId)` to
 * link it. The app only mints the flow's short-lived `clientToken` +
 * `publishableKey` with this call.
 */
export async function createAccountCollectionFlow(): Promise<AccountCollectionFlowResponse> {
  return getResponseData(
    axios.post<AccountCollectionFlowResponse>(
      ACCOUNT_COLLECTION_FLOW_PATH,
      undefined,
      getRequestConfig(RequestConfig.Default),
    ),
  )
}

/**
 * Complete the embedded flow — `POST /payments/account-collection-flow/{flowId}/
 * complete`. Called from the MT embed's `onSuccess` (MT fires no completion
 * webhook): the backend retrieves the flow from Modern Treasury and links the
 * funding External Account. Returns `{ status: 'COMPLETED', fundingAccount }`
 * (HTTP 200, linked — the connection now carries the funding account) or
 * `{ status: 'PENDING' }` (HTTP 202, MT has not finished — retry). HTTP 404
 * (unknown/foreign flow), 409 (KYB not active or the flow expired/cancelled),
 * and 502 (provider) surface through the normalized error channel.
 */
export async function completeAccountCollectionFlow(
  flowId: string,
): Promise<AccountCollectionFlowCompletionResponse> {
  // Guard the id in the fn (not just the call site): an empty id otherwise
  // builds `/account-collection-flow//complete` and fires it (see
  // src/queries/AGENTS.md).
  if (!flowId) {
    throw new Error('completeAccountCollectionFlow requires a flowId')
  }
  return getResponseData(
    axios.post<AccountCollectionFlowCompletionResponse>(
      `${ACCOUNT_COLLECTION_FLOW_PATH}/${encodeURIComponent(flowId)}/complete`,
      undefined,
      getRequestConfig(RequestConfig.Default),
    ),
  )
}

export async function initiateFundingAccountVerification(): Promise<FundingAccountVerificationResponse> {
  return getResponseData(
    axios.post<FundingAccountVerificationResponse>(
      FUNDING_ACCOUNT_VERIFICATION_PATH,
      undefined,
      getRequestConfig(RequestConfig.Default),
    ),
  )
}

export async function completeFundingAccountVerification(
  request: CompleteFundingAccountVerificationRequest,
): Promise<FundingAccountVerificationResponse> {
  const { amounts } = request
  if (
    amounts.length !== 2 ||
    amounts.some((amount) => !Number.isInteger(amount))
  ) {
    throw new Error(
      'completeFundingAccountVerification requires two whole-cent amounts',
    )
  }

  return getResponseData(
    axios.post<FundingAccountVerificationResponse>(
      `${FUNDING_ACCOUNT_VERIFICATION_PATH}/complete`,
      request,
      getRequestConfig(RequestConfig.Default),
    ),
  )
}
